import { createElement } from 'react';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';
import type { Sx } from '@/shared/types/uiTypes';

import { responsiveFontSizes } from './fontSizes';

type TypographyVariant =
  | 'body1'
  | 'body2'
  | 'caption'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2';

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  component?: keyof React.JSX.IntrinsicElements;
  variant?: TypographyVariant;
  color?: 'primary' | 'secondary' | 'error' | 'textSecondary' | string;
  size?: keyof typeof responsiveFontSizes;
  sx?: Sx;
  style?: CSSProperties;
}

const variantElement: Record<TypographyVariant, keyof React.JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'p',
  subtitle2: 'p',
  body1: 'p',
  body2: 'p',
  caption: 'span'
};

const colorClassName: Record<string, string> = {
  primary: 'text-primary',
  secondary: 'text-muted-foreground',
  error: 'text-destructive',
  textSecondary: 'text-muted-foreground'
};

const Text = ({
  children,
  className,
  color,
  component,
  size,
  style,
  sx: _sx,
  variant = 'body1',
  ...props
}: TypographyProps) => {
  const Component = component ?? variantElement[variant];
  const fontSize = size ? responsiveFontSizes[size].md : undefined;

  return createElement(Component, {
    className: cn(color && colorClassName[color], className),
    style: { fontSize, ...style },
    ...props,
    children
  });
};

export default Text;
